import React, { useState, useEffect } from 'react';
import { jobService } from '../../services/api';

export default function PartnerJobQueue({ partner }: { partner: any }) {
    const [jobs, setJobs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const res = await jobService.getAssignedJobs();
            setJobs(res.data.jobs || []);
        } catch (err) {
            console.error('Failed to load jobs', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    const handleAccept = async (jobId: string) => {
        try {
            await jobService.acceptJob(jobId);
            fetchJobs();
        } catch (err) {
            alert('Failed to accept job');
        }
    };

    const handleDecline = async (jobId: string) => {
        if (!window.confirm('Are you sure you want to decline this job? This will send it back to the dispatcher.')) return;
        try {
            await jobService.declineJob(jobId);
            fetchJobs();
        } catch (err) {
            alert('Failed to decline job');
        }
    };

    const handleComplete = async (jobId: string) => {
        if (!window.confirm('Mark this job as completed?')) return;
        try {
            await jobService.completeJob(jobId);
            fetchJobs();
        } catch (err) {
            alert('Failed to complete job');
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'dispatched': return <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-md">ACTION REQUIRED</span>;
            case 'accepted': return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-md">IN PROGRESS</span>;
            case 'completed': return <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-md">COMPLETED</span>;
            default: return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-bold rounded-md">{status.toUpperCase()}</span>;
        }
    };

    return (
        <div className="flex-1 p-4 md:p-8 max-w-7xl mx-auto w-full">
            <div className="mb-8">
                <h2 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">Welcome back, {partner?.contactName}</h2>
                <p className="text-gray-500 font-medium text-sm mt-1">Your trade network status is active. Jobs dispatched from <strong>partners@compliance-engine.io</strong></p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mb-8">
                <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mb-4">
                        <i className="fas fa-exclamation-circle text-xl"></i>
                    </div>
                    <div className="text-3xl font-black text-gray-900 mb-1">
                        {jobs.filter(j => j.status === 'dispatched').length}
                    </div>
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Pending Acceptance</div>
                </div>
                <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-4">
                        <i className="fas fa-tools text-xl"></i>
                    </div>
                    <div className="text-3xl font-black text-gray-900 mb-1">
                        {jobs.filter(j => j.status === 'accepted').length}
                    </div>
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">In Progress</div>
                </div>
                <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mb-4">
                        <i className="fas fa-check-circle text-xl"></i>
                    </div>
                    <div className="text-3xl font-black text-gray-900 mb-1">
                        {jobs.filter(j => j.status === 'completed').length}
                    </div>
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Completed Jobs</div>
                </div>
            </div>

            <div className="bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden mb-8">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <i className="fas fa-clipboard-list text-emerald-600"></i> My Job Queue
                    </h3>
                    <button onClick={fetchJobs} className="text-sm font-bold text-gray-500 hover:text-emerald-600 transition">
                        <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                    </button>
                </div>

                <div className="p-4 bg-gray-50">
                    {loading ? (
                        <div className="text-center py-10 text-gray-500">Loading jobs...</div>
                    ) : jobs.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-200">
                            <i className="fas fa-check-circle text-4xl text-gray-300 mb-3"></i>
                            <h4 className="text-lg font-bold text-gray-900">Queue is empty</h4>
                            <p className="text-gray-500 mt-1">You have no active or pending jobs assigned to you.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {jobs.map(job => (
                                <div key={job.id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:border-emerald-200 transition-colors">
                                    <div className="flex flex-col md:flex-row justify-between md:items-start gap-4 mb-4">
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                {getStatusBadge(job.status)}
                                                <span className="text-xs font-mono text-gray-400 md:hidden">{new Date(job.createdAt).toLocaleDateString()}</span>
                                            </div>
                                            <h4 className="text-lg font-bold text-gray-900">{job.serviceType}</h4>
                                            <p className="text-sm font-medium text-gray-600 max-w-xl">{job.testDetails}</p>
                                        </div>
                                        <div className="text-right hidden md:block">
                                            <span className="text-xs font-mono text-gray-400 block mb-1">Dispatched</span>
                                            <span className="text-sm font-bold text-gray-600">{new Date(job.dispatchedAt || job.createdAt).toLocaleDateString()}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl mb-4 text-sm">
                                        <div>
                                            <span className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">Address</span>
                                            <span className="font-semibold text-gray-900">{job.propertyAddress}</span>
                                        </div>
                                        <div>
                                            <span className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">Access Info</span>
                                            <span className="font-semibold text-gray-900">{job.accessConfirmation || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">Tenant Contact</span>
                                            <span className="font-semibold text-gray-900">{job.tenantName} - {job.tenantPhone}</span>
                                        </div>
                                        <div>
                                            <span className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">Appliance Locations</span>
                                            <span className="font-semibold text-gray-900">{job.applianceLocations || 'Not specified'}</span>
                                        </div>
                                    </div>

                                    {job.status === 'dispatched' && (
                                        <div className="flex items-center gap-3 pt-2">
                                            <button onClick={() => handleAccept(job.id)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-xl text-sm font-bold transition shadow-sm">
                                                <i className="fas fa-check mr-2"></i> Accept Job
                                            </button>
                                            <button onClick={() => handleDecline(job.id)} className="px-4 py-2.5 bg-white border border-gray-200 text-gray-600 hover:text-red-600 hover:border-red-200 hover:bg-red-50 rounded-xl text-sm font-bold transition">
                                                Decline
                                            </button>
                                        </div>
                                    )}

                                    {job.status === 'accepted' && (
                                        <div className="flex items-center gap-3 pt-2">
                                            <button onClick={() => handleComplete(job.id)} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-sm font-bold transition shadow-sm">
                                                <i className="fas fa-check-double mr-2"></i> Mark Completed
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* YELLOW CARD ALERT IF THEY ACTUALLY HAVE ONE */}
            {(partner?.strikesCount || 0) > 0 && (
                <div className="mb-8 bg-red-50 border border-red-100 rounded-2xl p-6 flex items-center gap-6">
                    <div className="w-14 h-14 bg-red-100 text-red-600 rounded-xl flex items-center justify-center text-2xl shrink-0">
                        <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <div>
                        <h4 className="text-red-900 font-black tracking-tight text-lg">Yellow Card Issued ({partner.strikesCount}/2)</h4>
                        <p className="text-red-700/80 font-medium">A violation has been recorded on your account: <span className="font-bold underline">{partner.lastStrikeReason}</span>. A second strike will result in permanent account termination.</p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-900 text-white p-8 rounded-3xl relative overflow-hidden">
                    <div className="absolute -right-4 -bottom-4 opacity-10 text-9xl">
                        <i className="fas fa-book-open"></i>
                    </div>
                    <div className="flex items-center gap-4 mb-6 relative z-10">
                        <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-xl">
                            <i className="fas fa-file-invoice"></i>
                        </div>
                        <h3 className="text-xl font-black">Trade Compliance Toolkit</h3>
                    </div>
                    <ul className="space-y-4 relative z-10 font-medium text-gray-300">
                        <li className="flex items-start gap-3">
                            <i className="fas fa-check mt-1 text-emerald-400"></i>
                            Upload your completed certificates directly via the partner portal
                        </li>
                        <li className="flex items-start gap-3">
                            <i className="fas fa-check mt-1 text-emerald-400"></i>
                            Access free gas/electrical certificate templates if needed
                        </li>
                        <li className="flex items-start gap-3">
                            <i className="fas fa-check mt-1 text-emerald-400"></i>
                            Use automated tenant notification for access issues
                        </li>
                    </ul>
                </div>

                <div className="bg-white border text-gray-400 border-gray-100 p-8 rounded-3xl">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center text-xl font-bold">
                            <i className="fas fa-info-circle"></i>
                        </div>
                        <h3 className="text-xl font-black text-gray-400">Policy Reference (For Info)</h3>
                    </div>
                    <ul className="space-y-4">
                        <li className="flex items-start gap-4 opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all">
                            <div className="bg-yellow-400 w-3 h-6 rounded flex-shrink-0 mt-0.5"></div>
                            <div>
                                <span className="font-bold text-gray-500 block">Yellow Card Policy (Reference)</span>
                                <span className="text-sm text-gray-400 font-medium leading-relaxed">Two minor infractions (e.g. late arrivals, poor communication) will result in account termination.</span>
                            </div>
                        </li>
                        <li className="flex items-start gap-4 opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all">
                            <div className="bg-red-600 w-3 h-6 rounded flex-shrink-0 mt-0.5 shadow-[0_0_10px_rgba(220,38,38,0.5)]"></div>
                            <div>
                                <span className="font-bold text-gray-500 block">Red Card (Zero Tolerance)</span>
                                <span className="text-sm text-gray-400 font-medium leading-relaxed">Immediate removal if you disappoint a customer after accepting a job on the portal. We guarantee reliability to our landlords.</span>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
